import { useState } from "react";
import { PageHero } from "@/pages/About";
import { Field, TextInput, TextArea, SelectField, ConsentCheckbox, Honeypot } from "@/components/FormFields";
import { useFormSubmit } from "@/lib/useFormSubmit";

const SUPPORT_CATEGORIES = [
    { value: "food", label: "Food and household essentials" },
    { value: "disability", label: "Disability / mobility support" },
    { value: "education", label: "Education / school support" },
    { value: "health", label: "Health / medical" },
    { value: "elderly", label: "Support for elderly member" },
    { value: "livelihoods", label: "Livelihood / enterprise" },
    { value: "other", label: "Other" },
];

export default function WarrenParkConnect() {
    return (
        <div>
            <PageHero title="Warren Park Connect" subtitle="Community information & referral platform" />

            <section className="gsf-section" data-testid="wpc-intro">
                <div className="gsf-container grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow">Connecting</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Skills. Needs. Opportunities.
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-5 text-[color:var(--gsf-body)] leading-relaxed">
                        <p>
                            Warren Park Connect is a community information and referral initiative designed to help
                            organise community needs, skills, trades, services and support requests. It enables more
                            responsible programme planning and helps residents connect with relevant opportunities
                            and service providers.
                        </p>
                        <p className="text-sm text-gray-500">
                            Your privacy matters. All information submitted is handled securely and reviewed inside the
                            Foundation’s admin dashboard — private beneficiary information is never displayed publicly.
                        </p>
                    </div>
                </div>
            </section>

            <section id="register-skills" className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="wpc-skills-section">
                <div className="gsf-container grid md:grid-cols-2 gap-10">
                    <div>
                        <div className="gsf-eyebrow">Register Your Skills</div>
                        <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Skills & Trades Registration</h2>
                        <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                            Share your skills, trades and services so we can match you with community opportunities,
                            referrals and programme activities.
                        </p>
                    </div>
                    <SkillsForm />
                </div>
            </section>

            <section id="request-support" className="gsf-section" data-testid="wpc-support-section">
                <div className="gsf-container grid md:grid-cols-2 gap-10">
                    <div>
                        <div className="gsf-eyebrow">Community Support</div>
                        <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Request Community Support</h2>
                        <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                            Submit a support request. Requests are reviewed responsibly and are never displayed publicly.
                        </p>
                    </div>
                    <SupportForm />
                </div>
            </section>
        </div>
    );
}

function SkillsForm() {
    const [state, setState] = useState({ full_name: "", email: "", phone: "", area: "", skills: "", availability: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("skills_registration");

    if (done) return <SuccessCard testid="skills-success">Thank you — your skills have been registered. Our team may contact you about relevant opportunities.</SuccessCard>;

    async function onSubmit(e) {
        e.preventDefault();
        await submit(state, consent);
    }

    return (
        <form onSubmit={onSubmit} className="gsf-card p-8 space-y-4" data-testid="skills-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="s-name" label="Full name" required>
                    <TextInput id="s-name" testid="skills-name" required value={state.full_name} onChange={(v) => setState({ ...state, full_name: v })} />
                </Field>
                <Field id="s-email" label="Email" required>
                    <TextInput id="s-email" testid="skills-email" type="email" required value={state.email} onChange={(v) => setState({ ...state, email: v })} />
                </Field>
                <Field id="s-phone" label="Phone / WhatsApp">
                    <TextInput id="s-phone" testid="skills-phone" value={state.phone} onChange={(v) => setState({ ...state, phone: v })} />
                </Field>
                <Field id="s-area" label="Area of residence">
                    <TextInput id="s-area" testid="skills-area" value={state.area} onChange={(v) => setState({ ...state, area: v })} />
                </Field>
            </div>
            <Field id="s-skills" label="Skills / trades / services" required>
                <TextArea id="s-skills" testid="skills-skills" required value={state.skills} onChange={(v) => setState({ ...state, skills: v })} rows={3} />
            </Field>
            <Field id="s-avail" label="Availability">
                <TextInput id="s-avail" testid="skills-availability" value={state.availability} onChange={(v) => setState({ ...state, availability: v })} />
            </Field>
            <Honeypot value={state.website} onChange={(v) => setState({ ...state, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="skills-consent" />
            {error && <p className="text-sm text-red-600" data-testid="skills-error">{error}</p>}
            <button type="submit" disabled={submitting} className="gsf-btn-primary disabled:opacity-60" data-testid="skills-submit">
                {submitting ? "Submitting…" : "Register My Skills"}
            </button>
        </form>
    );
}

function SupportForm() {
    const [state, setState] = useState({ full_name: "", contact: "", household: "", category: "", details: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("community_support");

    if (done) return <SuccessCard testid="support-success">Your request has been received. A team member will review it responsibly and follow up if additional information is needed.</SuccessCard>;

    async function onSubmit(e) {
        e.preventDefault();
        await submit(state, consent);
    }

    return (
        <form onSubmit={onSubmit} className="gsf-card p-8 space-y-4" data-testid="support-form">
            <Field id="cs-name" label="Full name" required>
                <TextInput id="cs-name" testid="support-name" required value={state.full_name} onChange={(v) => setState({ ...state, full_name: v })} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="cs-contact" label="Contact (email or phone)" required>
                    <TextInput id="cs-contact" testid="support-contact" required value={state.contact} onChange={(v) => setState({ ...state, contact: v })} />
                </Field>
                <Field id="cs-house" label="Household size">
                    <TextInput id="cs-house" testid="support-household" value={state.household} onChange={(v) => setState({ ...state, household: v })} />
                </Field>
            </div>
            <Field id="cs-cat" label="Category of support" required>
                <SelectField id="cs-cat" testid="support-category" value={state.category} onChange={(v) => setState({ ...state, category: v })}
                    placeholder="Select a category"
                    options={SUPPORT_CATEGORIES}
                />
            </Field>
            <Field id="cs-details" label="Please describe your request" required help="Do not include national ID numbers or sensitive medical details here. A team member will follow up.">
                <TextArea id="cs-details" testid="support-details" required value={state.details} onChange={(v) => setState({ ...state, details: v })} rows={4} />
            </Field>
            <Honeypot value={state.website} onChange={(v) => setState({ ...state, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="support-consent" />
            {error && <p className="text-sm text-red-600" data-testid="support-error">{error}</p>}
            <button type="submit" disabled={submitting} className="gsf-btn-primary disabled:opacity-60" data-testid="support-submit">
                {submitting ? "Submitting…" : "Submit Support Request"}
            </button>
        </form>
    );
}

export function SuccessCard({ children, testid }) {
    return (
        <div className="gsf-card p-8 border-l-4 border-l-[color:var(--gsf-green-700)]" data-testid={testid}>
            <div className="gsf-eyebrow">Received</div>
            <p className="mt-3 text-[color:var(--gsf-body)] leading-relaxed">{children}</p>
        </div>
    );
}
